import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { StringParam, useQueryParams, withDefault, DelimitedArrayParam } from 'use-query-params';

import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';

export const useInsightsParams = () => {
  const { pathname } = useLocation();

  const [query, setQuery] = useQueryParams({
    [INSIGHTS_QUERY_KEY.name]: withDefault(StringParam, null),
    [INSIGHTS_QUERY_KEY.query_token]: withDefault(StringParam, getDefaultToken()),
    [INSIGHTS_QUERY_KEY.title]: withDefault(StringParam, null),
    [INSIGHTS_QUERY_KEY.include_keywords]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.exclude_keywords]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.include_named_entities]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.exclude_named_entities]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.include_topics]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.exclude_topics]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.include_text]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.exclude_text]: withDefault(DelimitedArrayParam, []),
    [INSIGHTS_QUERY_KEY.duration]: withDefault(StringParam, null),
    [INSIGHTS_QUERY_KEY.adult_explicit_content]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.arms_ammunition]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.crime_harmful_act]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.death_injury_military_conflict]: withDefault(DelimitedArrayParam, [
      'unrelated',
    ]),
    [INSIGHTS_QUERY_KEY.debated_social]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.hate_speech]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.illegal_drugs]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.obscenity]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.online_piracy]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.spam_harmful]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.terrorism]: withDefault(DelimitedArrayParam, ['unrelated']),
    [INSIGHTS_QUERY_KEY.include_linked_named_entities]: withDefault(StringParam, null),
    [INSIGHTS_QUERY_KEY.exclude_linked_named_entities]: withDefault(StringParam, null),
  });

  function setSearchTerm(searchTerm: string) {
    if (!searchTerm) {
      setQuery({ [INSIGHTS_QUERY_KEY.name]: undefined });
    } else {
      setQuery({ [INSIGHTS_QUERY_KEY.name]: searchTerm });
    }
  }

  function getDefaultToken(): string {
    const paths = pathname.split('/');
    if (paths.includes('integrations') && paths.includes('megaphone')) {
      return defaultMegaphoneToken;
    }

    if (paths.includes('integrations') && paths.includes('ihm')) {
      return defaultIHMToken;
    }

    return EMPTY_FILTER_TOKEN;
  }

  function setQueryToken(token?: string | null) {
    if (!token || token === getDefaultToken()) {
      setQuery({ [INSIGHTS_QUERY_KEY.query_token]: undefined });
    } else {
      setQuery({ [INSIGHTS_QUERY_KEY.query_token]: token });
    }
  }

  const isDefaultQueryToken = useMemo(
    () => query.query_token === getDefaultToken(),
    [query.query_token, getDefaultToken, pathname]
  );

  const isEmptyQueryToken = useMemo(() => {
    return query.query_token === EMPTY_FILTER_TOKEN;
  }, [query.query_token]);

  return {
    query,
    setQuery,
    queryToken: query.query_token,
    setQueryToken,
    isDefaultQueryToken,
    isEmptyQueryToken,
    searchTerm: query.name,
    setSearchTerm,
  };
};

export const EMPTY_FILTER_TOKEN = 'JTdCJTIyYWxsJTIyJTNBJTVCJTVEJTdE';

// at the moment we don't use this but we might in the future
// encoded string for {all:[brand_safety_garm: {all-categories: {range: {gte: unrelated, lte: floor}}}]}
const allGarmUnrelatedToFloor =
  'JTdCJTIyYWxsJTIyJTNBJTVCJTdCJTIyYnJhbmRfc2FmZXR5X2dhcm0lMjIlM0ElN0IlMjJhZHVsdF9leHBsaWNpdF9jb250ZW50JTIyJTNBJTdCJTIycmFuZ2UlMjIlM0ElN0IlMjJndGUlMjIlM0ElMjJ1bnJlbGF0ZWQlMjIlMkMlMjJsdGUlMjIlM0ElMjJmbG9vciUyMiU3RCU3RCUyQyUyMmFybXNfYW1tdW5pdGlvbiUyMiUzQSU3QiUyMnJhbmdlJTIyJTNBJTdCJTIyZ3RlJTIyJTNBJTIydW5yZWxhdGVkJTIyJTJDJTIybHRlJTIyJTNBJTIyZmxvb3IlMjIlN0QlN0QlMkMlMjJjcmltZV9oYXJtZnVsX2FjdCUyMiUzQSU3QiUyMnJhbmdlJTIyJTNBJTdCJTIyZ3RlJTIyJTNBJTIydW5yZWxhdGVkJTIyJTJDJTIybHRlJTIyJTNBJTIyZmxvb3IlMjIlN0QlN0QlMkMlMjJkZWF0aF9pbmp1cnlfbWlsaXRhcnlfY29uZmxpY3QlMjIlM0ElN0IlMjJyYW5nZSUyMiUzQSU3QiUyMmd0ZSUyMiUzQSUyMnVucmVsYXRlZCUyMiUyQyUyMmx0ZSUyMiUzQSUyMmZsb29yJTIyJTdEJTdEJTJDJTIyZGViYXRlZF9zb2NpYWwlMjIlM0ElN0IlMjJyYW5nZSUyMiUzQSU3QiUyMmd0ZSUyMiUzQSUyMnVucmVsYXRlZCUyMiUyQyUyMmx0ZSUyMiUzQSUyMmZsb29yJTIyJTdEJTdEJTJDJTIyaGF0ZV9zcGVlY2glMjIlM0ElN0IlMjJyYW5nZSUyMiUzQSU3QiUyMmd0ZSUyMiUzQSUyMnVucmVsYXRlZCUyMiUyQyUyMmx0ZSUyMiUzQSUyMmZsb29yJTIyJTdEJTdEJTJDJTIyaWxsZWdhbF9kcnVncyUyMiUzQSU3QiUyMnJhbmdlJTIyJTNBJTdCJTIyZ3RlJTIyJTNBJTIydW5yZWxhdGVkJTIyJTJDJTIybHRlJTIyJTNBJTIyZmxvb3IlMjIlN0QlN0QlMkMlMjJvYnNjZW5pdHklMjIlM0ElN0IlMjJyYW5nZSUyMiUzQSU3QiUyMmd0ZSUyMiUzQSUyMnVucmVsYXRlZCUyMiUyQyUyMmx0ZSUyMiUzQSUyMmZsb29yJTIyJTdEJTdEJTJDJTIyb25saW5lX3BpcmFjeSUyMiUzQSU3QiUyMnJhbmdlJTIyJTNBJTdCJTIyZ3RlJTIyJTNBJTIydW5yZWxhdGVkJTIyJTJDJTIybHRlJTIyJTNBJTIyZmxvb3IlMjIlN0QlN0QlMkMlMjJzcGFtX2hhcm1mdWwlMjIlM0ElN0IlMjJyYW5nZSUyMiUzQSU3QiUyMmd0ZSUyMiUzQSUyMnVucmVsYXRlZCUyMiUyQyUyMmx0ZSUyMiUzQSUyMmZsb29yJTIyJTdEJTdEJTJDJTIydGVycm9yaXNtJTIyJTNBJTdCJTIycmFuZ2UlMjIlM0ElN0IlMjJndGUlMjIlM0ElMjJ1bnJlbGF0ZWQlMjIlMkMlMjJsdGUlMjIlM0ElMjJmbG9vciUyMiU3RCU3RCU3RCU3RCU1RCU3RA==';

// encoded string for {all:[]}
const defaultMegaphoneToken = 'JTdCJTIyYWxsJTIyJTNBJTVCJTVEJTdE';

// encoded string for {all:[]}
const defaultIHMToken = 'JTdCJTIyYWxsJTIyJTNBJTVCJTVEJTdE';
